import {pluralize} from 'BenchlingUtil';
import notifications from 'notifications';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {checkClipboardReadAccess} from 'util/clipboard/ClipboardUtil';

import {ParseClipboardContentFn} from './CopyPasteTypes';
import {parseDatasetIdsClipboardContent} from './parseDatasetIdsClipboardContent';
import {parsePlateIdsClipboardContent} from './parsePlateIdsClipboardContent';
import {parseStepUpsertsClipboardContent} from './parseStepUpsertsClipboardContent';

const COULD_NOT_READ_CLIPBOARD_MSG = 'Unable to read from clipboard.';
const INVALID_CLIPBOARD_CONTENT_MSG = 'Could not paste steps, because the clipboard does not contain steps.';

const PARSE_CLIPBOARD_CONTENT_FNS: Array<ParseClipboardContentFn> = [
  parseStepUpsertsClipboardContent,
  parsePlateIdsClipboardContent,
  parseDatasetIdsClipboardContent,
];

async function readClipboardText(): Promise<string | null> {
  // `checkClipboardReadAccess` shows its own notification if access is denied.
  if (!(await checkClipboardReadAccess())) {
    return null;
  }
  try {
    return await navigator.clipboard.readText();
  } catch (e) {
    // `readText` can still throw a `DOMException` even after `checkClipboardReadAccess` passed.
    if (e instanceof DOMException) {
      notifications.error(COULD_NOT_READ_CLIPBOARD_MSG);
      return null;
    }
    throw e;
  }
}

export async function pasteStepsFromClipboard(
  pipeline: Pick<PipelineGraphQL, 'steps' | 'id'>
): Promise<{createdStepIds: Array<string>}> {
  const text = await readClipboardText();
  if (text == null) {
    return {createdStepIds: []};
  }

  let steps = null;
  for (const parseFn of PARSE_CLIPBOARD_CONTENT_FNS) {
    steps = await parseFn(pipeline.id, text);
    if (steps != null) {
      break;
    }
  }
  if (steps == null || steps.length === 0) {
    notifications.error(INVALID_CLIPBOARD_CONTENT_MSG);
    return {createdStepIds: []};
  }

  const {newStepIds} = await PipelineDAO.addAndUpdatePipelineSteps(pipeline.id, {
    stepsToAdd: steps,
    stepsToUpdate: [],
  });
  notifications.success(`Pasted ${newStepIds.length} ${pluralize(newStepIds.length, 'step')} from clipboard`);

  return {createdStepIds: newStepIds};
}
